import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  Link,
  Grid,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControlLabel,
  Checkbox,
  IconButton,
  Tooltip,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { loginUser, clearAuthCache, friendlyAuthError } from '../firebase';
import { useSecurity } from '../contexts/SecurityContext';
import { RateLimiter, sanitizeRedirectUrl } from '../utils/security';
import Header from './Header';
import MFAVerification from './security/MFAVerification';
import NetworkErrorDialog from './NetworkErrorDialog';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import { passkeysSupported, passkeysEnabled, signInWithPasskey } from '../services/passkeyClient';
import { getMultiFactorResolver, MultiFactorResolver } from 'firebase/auth';
import { auth } from '../firebase';

const TRUSTED_REDIRECT_HOSTS =
  (process.env.REACT_APP_TRUSTED_REDIRECT_HOSTS || 'superior-payroll-cw.web.app,superior-solutions.com')
    .split(',')
    .map((h) => h.trim())
    .filter(Boolean);

// Module-scope RateLimiter so its in-memory state persists across renders.
const rateLimiter = new RateLimiter(
  Number(process.env.REACT_APP_MAX_LOGIN_ATTEMPTS) || 5,
  Number(process.env.REACT_APP_LOGIN_LOCKOUT_TIME) || 300000
);

const getClientIdentifier = (): string => {
  const userAgent = navigator.userAgent;
  const screenProps = `${window.screen.width}x${window.screen.height}x${window.screen.colorDepth}`;
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  let hash = 0;
  const str = `${userAgent}|${screenProps}|${timezone}`;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash &= hash;
  }
  return hash.toString();
};

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [initializingAuth, setInitializingAuth] = useState(true);
  const [tooManyAttempts, setTooManyAttempts] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [sessionTimeoutOpen, setSessionTimeoutOpen] = useState(false);
  const [mfaResolver, setMfaResolver] = useState<MultiFactorResolver | null>(null);
  const [networkErrorOpen, setNetworkErrorOpen] = useState(false);
  const [staySignedIn, setStaySignedIn] = useState(false);

  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const { verifyCaptcha } = useSecurity();

  const getRedirectUrl = () => {
    const params = new URLSearchParams(location.search);
    const redirectUrl = params.get('redirect');
    const sessionRedirect = sessionStorage.getItem('redirectAfterLogin');
    if (sessionRedirect) {
      sessionStorage.removeItem('redirectAfterLogin');
      return sanitizeRedirectUrl(sessionRedirect, TRUSTED_REDIRECT_HOSTS);
    }
    return sanitizeRedirectUrl(redirectUrl || '/dashboard', TRUSTED_REDIRECT_HOSTS);
  };

  useEffect(() => {
    const initAuth = async () => {
      try {
        const remembered = localStorage.getItem('rememberedEmail');
        if (remembered) {
          setEmail(remembered);
          setStaySignedIn(true);
        }
      } finally {
        setInitializingAuth(false);
      }
    };

    initAuth();

    const clientId = getClientIdentifier();
    if (rateLimiter.isRateLimited(clientId)) {
      setTooManyAttempts(true);
      startCountdown(clientId);
    }

    return () => {
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startCountdown = (clientId: string) => {
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    const now = Date.now();
    const attempts: number[] = (rateLimiter as any).attempts?.[clientId] || [];
    if (attempts.length === 0) {
      setTooManyAttempts(false);
      return;
    }
    const oldestAttempt = Math.min(...attempts);
    const lockoutTime = Number(process.env.REACT_APP_LOGIN_LOCKOUT_TIME) || 300000;
    const remaining = Math.max(0, oldestAttempt + lockoutTime - now);
    setTimeRemaining(Math.ceil(remaining / 1000));
    if (remaining <= 0) {
      setTooManyAttempts(false);
      rateLimiter.clearAttempts(clientId);
      return;
    }
    countdownIntervalRef.current = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          setTooManyAttempts(false);
          rateLimiter.clearAttempts(clientId);
          if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const clientId = getClientIdentifier();

    if (rateLimiter.isRateLimited(clientId)) {
      setTooManyAttempts(true);
      startCountdown(clientId);
      setLoading(false);
      return;
    }

    try {
      // Don't HTML-escape an email — that breaks legitimate addresses like o'brien@x.com.
      const cleanEmail = email.trim();
      const cleanPassword = password;

      // reCAPTCHA is best-effort: if it can't issue a token (misconfigured
      // domain, blocked script, offline), warn but still let Firebase auth run.
      // Hard-blocking here would lock everyone out on any captcha hiccup.
      const captchaToken = await verifyCaptcha();
      if (!captchaToken && process.env.REACT_APP_RECAPTCHA_SITE_KEY) {
        console.warn('reCAPTCHA token unavailable; proceeding with sign-in (fail-open).');
      }

      const result = await loginUser(cleanEmail, cleanPassword, staySignedIn);

      if (result.success && result.user) {
        if (staySignedIn) localStorage.setItem('rememberedEmail', cleanEmail);
        else localStorage.removeItem('rememberedEmail');
        navigate(result.redirectUrl || getRedirectUrl());
      } else {
        rateLimiter.addAttempt(clientId);
        if (rateLimiter.isRateLimited(clientId)) {
          setTooManyAttempts(true);
          startCountdown(clientId);
        }

        // Firebase MFA challenge: if the user has a second factor, the
        // signInWithEmailAndPassword call inside loginUser threw a
        // multi-factor-auth-required error. Surface the resolver to the user.
        if ((result as any).mfaError) {
          const resolver = getMultiFactorResolver(auth, (result as any).mfaError);
          setMfaResolver(resolver);
          setLoading(false);
          return;
        }

        if (result.pendingApproval) {
          setError(result.error || 'Your account is pending approval by an administrator.');
        } else if (result.error && result.error.includes('network')) {
          setNetworkErrorOpen(true);
        } else {
          setError(result.error || 'Failed to log in');
        }
      }
    } catch (err: any) {
      console.error('Login error:', err);
      rateLimiter.addAttempt(clientId);
      const msg = err.message || '';
      if (/network|offline|internet|connection/i.test(msg)) {
        setNetworkErrorOpen(true);
      } else {
        setError(msg || 'An error occurred');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasskeyLogin = async () => {
    setError('');
    setLoading(true);
    try {
      await signInWithPasskey();
      navigate(getRedirectUrl());
    } catch (err: any) {
      console.error('Passkey login error:', err?.code || '(no code)', err);
      // An unreachable passkey backend (IAM-403'd callable → unparseable HTML 403)
      // surfaces as functions/internal. Don't show the generic "configuration issue"
      // sign-in copy for that — it's a passkey-availability problem, not the user's.
      const code: string = err?.code || '';
      if (code === 'functions/internal' || code === 'internal' || code === 'functions/unavailable' || code === 'unavailable') {
        setError('Passkey sign-in is unavailable right now. Please sign in with your email and password.');
      } else if (err?.name === 'NotAllowedError' || err?.name === 'AbortError') {
        // User dismissed the OS passkey prompt — not an error worth alarming over.
        setError('');
      } else {
        setError(friendlyAuthError(err));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMFASuccess = () => {
    rateLimiter.clearAttempts(getClientIdentifier());
    setMfaResolver(null);
    navigate(getRedirectUrl());
  };

  const handleMFACancel = () => {
    clearAuthCache();
    setMfaResolver(null);
    setError('Two-factor authentication is required to sign in.');
  };

  const handleSessionTimeout = () => {
    setSessionTimeoutOpen(false);
    window.location.href = '/login?expired=true';
  };

  const handleNetworkErrorRetry = async () => {
    handleLogin({ preventDefault: () => {} } as React.FormEvent);
  };

  if (initializingAuth) {
    return (
      <Container maxWidth="xs">
        <Box sx={{ mt: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Initializing authentication...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <>
      <Header />
      <Container maxWidth="xs">
      <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Paper elevation={2} sx={{ p: 4, width: '100%', borderRadius: 2 }}>
          <Typography component="h1" variant="h5" sx={{ mb: 3, textAlign: 'center' }}>
            Sign in
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

          {tooManyAttempts && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              Too many failed login attempts. Please try again in {Math.floor(timeRemaining / 60)}
              :{(timeRemaining % 60).toString().padStart(2, '0')}.
            </Alert>
          )}

          {new URLSearchParams(location.search).get('expired') === 'true' && (
            <Alert severity="info" sx={{ mb: 3 }}>
              Your session has expired for security reasons. Please sign in again.
            </Alert>
          )}

          <Box component="form" onSubmit={handleLogin} sx={{ mt: 1 }}>
            {passkeysEnabled() && passkeysSupported() && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <Tooltip title="Sign in with passkey (Face ID / Touch ID / Windows Hello)">
                  <IconButton
                    color="primary"
                    onClick={handlePasskeyLogin}
                    disabled={loading || tooManyAttempts}
                    sx={{ width: 64, height: 64, border: '1px solid', borderColor: 'primary.main' }}
                  >
                    <FingerprintIcon fontSize="large" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}

            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading || tooManyAttempts}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading || tooManyAttempts}
            />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={staySignedIn}
                    onChange={(e) => setStaySignedIn(e.target.checked)}
                    color="primary"
                  />
                }
                label="Stay signed in"
              />
              <Link href="/forgot-password" variant="body2">
                Forgot Password?
              </Link>
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading || tooManyAttempts}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>

            <Divider sx={{ my: 2 }}>
              <Typography color="text.secondary" variant="body2">OR</Typography>
            </Divider>

            <Grid container justifyContent="center">
              <Grid item>
                <Link href="/register" variant="body2">
                  {"Don't have an account? Sign Up"}
                </Link>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Box>

      <Dialog open={sessionTimeoutOpen} onClose={() => setSessionTimeoutOpen(false)}>
        <DialogTitle>Session Expired</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your session has expired for security reasons. Please sign in again to continue.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSessionTimeout} color="primary" autoFocus>
            Sign In
          </Button>
        </DialogActions>
      </Dialog>

      <MFAVerification
        open={!!mfaResolver}
        resolver={mfaResolver}
        onSuccess={handleMFASuccess}
        onCancel={handleMFACancel}
      />

      <NetworkErrorDialog
        open={networkErrorOpen}
        onClose={() => setNetworkErrorOpen(false)}
        onRetry={handleNetworkErrorRetry}
        errorType="auth"
      />
      </Container>
    </>
  );
};

export default Login;
